import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  header: { marginBottom: '32px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '32px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '24px', maxWidth: '600px',
  },
  sectionTitle: {
    fontSize: '13px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  settingRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 0', borderBottom: '1px solid #f3f4f6',
  },
  settingLabel: { fontSize: '14px', fontWeight: '600', color: '#111827' },
  settingDesc: { fontSize: '12px', color: '#6b7280', marginTop: '2px' },
  dangerBtn: {
    backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  secondaryBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  success: {
    backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px',
  },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px',
  },
};

export default function Settings() {
  const { currentUser } = useAuth();
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handlePasswordReset() {
    setLoading(true);
    setResetError('');
    try {
      const res = await fetch(`${API}/api/email/send-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: currentUser.email }),
      });
      if (!res.ok) throw new Error('Failed');
      setResetSent(true);
    } catch {
      setResetError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <div style={styles.header}>
        <div style={styles.title}>Settings</div>
        <div style={styles.sub}>Manage your account preferences and security</div>
      </div>

      {/* Security */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Security</div>

        {resetSent && <div style={styles.success}>✓ Password reset email sent to {currentUser?.email}</div>}
        {resetError && <div style={styles.error}>{resetError}</div>}

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Password</div>
            <div style={styles.settingDesc}>Send a password reset link to your email</div>
          </div>
          <button style={styles.secondaryBtn} onClick={handlePasswordReset} disabled={loading || resetSent}>
            {loading ? 'Sending...' : resetSent ? 'Email Sent' : 'Reset Password'}
          </button>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Email Address</div>
            <div style={styles.settingDesc}>{currentUser?.email}</div>
          </div>
          <div style={{
            fontSize: '11px', fontWeight: '700',
            color: currentUser?.emailVerified ? '#16a34a' : '#d97706',
            backgroundColor: currentUser?.emailVerified ? '#dcfce7' : '#fef3c7',
            padding: '3px 10px', borderRadius: '20px',
          }}>
            {currentUser?.emailVerified ? '✓ Verified' : '⚠ Unverified'}
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Danger Zone</div>
        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Delete Account</div>
            <div style={styles.settingDesc}>Permanently delete your account and all data. This cannot be undone.</div>
          </div>
          <button style={styles.dangerBtn} onClick={() => alert('Please contact support to delete your account.')}>
            Delete Account
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
